import { useState, useRef, useEffect, useCallback } from "react";
import AdminPortal from './AdminPortal';
import AUSOVRN_LOGO from './ausovrn-header.png';

const BACKEND_URL = process.env.NODE_ENV === "development" ? "http://localhost:3001" : "";

const NAVY     = "#0f2d51";
const NAVY_MID = "#112f54";
const OFFWHITE = "#e6edf5";
const TEAL     = "#0D9488";
const TEAL_LT  = "#14B8A6";
const FONT     = "Aptos, Calibri, 'Segoe UI', Arial, sans-serif";

// ── Category colours ──────────────────────────────────────────────────────────
const categoryColors = {
  "HR & People":           { bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE" },
  "Security":              { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" },
  "ICT & Security":        { bg: "#FFF7ED", text: "#EA580C", border: "#FED7AA" },
  "Finance & Procurement": { bg: "#F0FDF4", text: "#16A34A", border: "#BBF7D0" },
  "Privacy & Compliance":  { bg: "#FAF5FF", text: "#7C3AED", border: "#DDD6FE" },
};

// ── Induction checklist ───────────────────────────────────────────────────────
const INDUCTION_CHECKLIST = [
  { id: 1, title: "APS Code of Conduct",              docId: "doc-001", time: "20 min", urgent: true  },
  { id: 2, title: "Protective Security Awareness",    docId: "doc-002", time: "30 min", urgent: true  },
  { id: 3, title: "Work Health and Safety",           docId: "doc-008", time: "15 min", urgent: true  },
  { id: 4, title: "Privacy and Information Handling", docId: "doc-007", time: "20 min", urgent: false },
  { id: 5, title: "Onboarding Guidelines",            docId: "doc-006", time: "10 min", urgent: false },
];

// ── Common queries (per Chris's brief) ────────────────────────────────────────
const SUGGESTED = [
  "APS Code of Conduct obligations",
  "WHS incident reporting requirements",
  "Procurement thresholds for open tender",
  "Essential Eight cybersecurity strategies",
  "Sharing OFFICIAL: Sensitive with contractors",
  "Clearance level for PROTECTED information",
  "PSPF changes between v2023.2 and v2024.1",
  "Privacy Act APP obligations",
];

// ── RAG retrieval ─────────────────────────────────────────────────────────────
function retrieveRelevantDocs(query, documentStore, topK = 3) {
  const q = query.toLowerCase();
  const scored = documentStore.map(doc => {
    const text = (doc.title + " " + doc.content + " " + doc.category).toLowerCase();
    const words = q.split(/\s+/).filter(w => w.length > 3);
    let score = 0;
    words.forEach(word => {
      const count = (text.match(new RegExp(word, "g")) || []).length;
      score += count;
      if (doc.title.toLowerCase().includes(word)) score += 5;
      if (doc.category.toLowerCase().includes(word)) score += 3;
    });
    return { doc, score };
  });
  return scored.filter(s => s.score > 0).sort((a, b) => b.score - a.score).slice(0, topK).map(s => s.doc);
}

// ── Claude API call ───────────────────────────────────────────────────────────
async function askClaude(question, relevantDocs, conversationHistory) {
  const docContext = relevantDocs.map(doc =>
    `[DOCUMENT: ${doc.title} | Version: ${doc.version} | Date: ${doc.date} | Classification: ${doc.classification}]\n${doc.content}\n[END DOCUMENT]`
  ).join("\n\n");

  const systemPrompt = `You are AUSOVRN™ Assist, a sovereign AI assistant for Australian Government agencies. You answer questions ONLY based on the provided document store.

RULES:
1. Answer ONLY from the documents provided. If the answer is not in the document store, respond: "This information is not available in the current document repository."
2. Structure every response clearly with:
   ANSWER: [your concise answer]
   SOURCE: [Document Title]
   VERSION: [version number and date]
3. Keep answers professional and concise — suitable for APS staff
4. Never fabricate policy details, figures or requirements
5. Uphold Australian Government values: integrity, accountability, respect

DOCUMENT STORE:
${docContext}`;

  const messages = [...conversationHistory, { role: "user", content: question }];
  const response = await fetch(`${BACKEND_URL}/api/ask`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ systemPrompt, messages })
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return data.answer;
}

// ═════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [documentStore, setDocumentStore]         = useState([]);
  const [docsLoading, setDocsLoading]             = useState(true);
  const [docsError, setDocsError]                 = useState(null);
  const [messages, setMessages]                   = useState([]);
  const [input, setInput]                         = useState("");
  const [loading, setLoading]                     = useState(false);
  const [activeTab, setActiveTab]                 = useState("chat");
  const [inductionProgress, setInductionProgress] = useState({});
  const [selectedDoc, setSelectedDoc]             = useState(null);
  const [searchTerm, setSearchTerm]               = useState("");
  const [conversationHistory, setConversationHistory] = useState([]);
  const chatEndRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    setDocsLoading(true);
    setDocsError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/documents`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDocumentStore(data.documents || []);
    } catch {
      setDocsError("Document repository unavailable. Check connection.");
    } finally {
      setDocsLoading(false);
    }
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // ── Opening message per Chris's brief ────────────────────────────────────────
  useEffect(() => {
    setMessages([{
      role: "assistant",
      text: "AUSOVRN™ Assist provides sovereign, policy-aware intelligence across your document environment. All responses are grounded in authoritative sources with citation and version control.",
      sources: [],
      timestamp: new Date()
    }]);
  }, []);

  const sendMessage = async (text) => {
    const question = text || input.trim();
    if (!question || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: question, timestamp: new Date() }]);
    setLoading(true);
    try {
      const relevantDocs = retrieveRelevantDocs(question, documentStore);
      const answer = await askClaude(question, relevantDocs, conversationHistory);
      setMessages(prev => [...prev, { role: "assistant", text: answer, sources: relevantDocs, timestamp: new Date() }]);
      setConversationHistory(prev => [
        ...prev,
        { role: "user", content: question },
        { role: "assistant", content: answer }
      ]);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: "assistant",
        text: `System error: ${e.message}`,
        sources: [],
        timestamp: new Date()
      }]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // ── Format response — highlight ANSWER / SOURCE / VERSION labels ──────────
  const formatText = (text) =>
    text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/^(ANSWER:|SOURCE:|VERSION:)/gm,
        `<span style="color:${NAVY};font-weight:700;font-family:${FONT};font-size:10px;letter-spacing:0.6px;text-transform:uppercase">$1</span>`)
      .replace(/\n/g, "<br/>");

  const filteredDocs = documentStore.filter(doc =>
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.owner.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const completedCount = INDUCTION_CHECKLIST.filter(i => inductionProgress[i.id]).length;

  return (
    <div style={{ fontFamily: FONT, background: "#EEF2F7", minHeight: "100vh", color: "#1a2535" }}>

      {/* ══ HEADER ══════════════════════════════════════════════════════════════ */}
      <header style={{
        background: `linear-gradient(to right, ${NAVY} 0%, ${NAVY_MID} 100%)`,
        boxShadow: "0 2px 12px rgba(0,0,0,0.4)"
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>

          {/* Logo row */}
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between",
            paddingTop: 14, paddingBottom: 2
          }}>
            {/* Logo — using the uploaded header image */}
          <img
            src={AUSOVRN_LOGO}
            alt="AUSOVRN™ Assist — Policy and Operational Intelligence"
            style={{
              height: 64,
              width: "auto",
              objectFit: "contain",
              display: "block",
              mixBlendMode: "screen",
              filter: "brightness(1.15) contrast(1.05)"
            }}
/>

            <span style={{
              fontSize: 14,
              color: OFFWHITE,
              opacity: 0.9,
              letterSpacing: "1.5px",
              fontFamily: FONT,
              textTransform: "uppercase",
              fontWeight: 700
            }}>
  AUSOVRN™ Assist · Proof of Concept
</span>
             
          </div>

          {/* Nav tabs */}
          <div style={{ display: "flex", gap: 2, marginTop: 12, overflowX: "auto" }}>
            {[
              { id: "chat",      label: "Ask Assist"          },
              { id: "induction", label: "Induction"            },
              { id: "docs",      label: "Document Repository"  },
              { id: "admin",     label: "Admin"                },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "9px 20px", whiteSpace: "nowrap",
                  background: activeTab === tab.id ? OFFWHITE : "transparent",
                  color: activeTab === tab.id ? NAVY : `${OFFWHITE}80`,
                  border: "none", borderRadius: "6px 6px 0 0",
                  cursor: "pointer", fontFamily: FONT, fontSize: 14,
                  fontWeight: activeTab === tab.id ? 700 : 500,
                  transition: "all 0.15s ease", letterSpacing: "0.3px"
                }}
              >{tab.label}</button>
            ))}
          </div>
        </div>
      </header>

      {/* ── System behaviour bar ─────────────────────────────────────────────── */}
      <div style={{
        background: "#152844",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        padding: "6px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between"
      }}>
        <span style={{ fontSize: 12, color: `${OFFWHITE}80`, fontFamily: FONT, letterSpacing: "0.3px" }}>
          Read-only environment · No document upload · Source-cited responses
        </span>
        <span style={{ fontSize: 12, color: `${OFFWHITE}55`, fontFamily: FONT }}>
          {docsLoading ? "Loading repository…" : `${documentStore.length} documents indexed`}
        </span>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 24px" }}>

        {/* Error banner */}
        {docsError && (
          <div style={{
            background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6,
            padding: "10px 16px", marginBottom: 12, fontSize: 14, color: "#DC2626",
            display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: FONT
          }}>
            ⚠️ {docsError}
            <button onClick={loadDocuments} style={{
              background: "#DC2626", color: "white", border: "none",
              borderRadius: 4, padding: "4px 10px", fontSize: 12,
              cursor: "pointer", fontFamily: FONT
            }}>Retry</button>
          </div>
        )}

        {/* ══ ASK ASSIST TAB ════════════════════════════════════════════════════ */}
        {activeTab === "chat" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>

            {/* Chat window */}
            <div style={{
              background: "white", borderRadius: 8,
              boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
              display: "flex", flexDirection: "column",
              height: "calc(100dvh - 230px)", minHeight: 400,
              border: "1px solid #cdd6e2"
            }}>
              {/* Messages */}
              <div style={{ flex: 1, overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: 16 }}>
                {messages.map((msg, i) => (
                  <div key={i} style={{
                    display: "flex",
                    flexDirection: msg.role === "user" ? "row-reverse" : "row",
                    gap: 10, alignItems: "flex-start"
                  }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 4, flexShrink: 0,
                      background: msg.role === "user" ? NAVY : "#152844",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 8, color: OFFWHITE, fontWeight: 700,
                      letterSpacing: "0.3px", fontFamily: FONT
                    }}>
                      {msg.role === "user" ? "YOU" : "AU"}
                    </div>
                    <div style={{ maxWidth: "78%", display: "flex", flexDirection: "column", gap: 5 }}>
                      <div style={{
                        background: msg.role === "user" ? NAVY : "#F4F7FB",
                        color: msg.role === "user" ? OFFWHITE : "#1a2535",
                        padding: "11px 15px",
                        borderRadius: msg.role === "user" ? "8px 2px 8px 8px" : "2px 8px 8px 8px",
                        fontSize: 13.5, lineHeight: 1.75,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.06)", fontFamily: FONT,
                        borderLeft: msg.role === "assistant" ? `3px solid ${TEAL}` : "none"
                      }}
                        dangerouslySetInnerHTML={{ __html: formatText(msg.text) }}
                      />
                      {msg.sources?.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {msg.sources.map((src, j) => (
                            <div
                              key={j}
                              onClick={() => { setSelectedDoc(src); setActiveTab("docs"); }}
                              style={{
                                background: "#EEF3FA", border: "1px solid #bfcfe0",
                                borderRadius: 4, padding: "3px 9px",
                                fontSize: 11, color: NAVY, cursor: "pointer",
                                fontFamily: FONT, display: "flex", alignItems: "center", gap: 4
                              }}
                            >
                              <span style={{ opacity: 0.55 }}>📄</span>
                              {src.title} · {src.version}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: FONT }}>
                        {msg.timestamp.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 4, background: "#152844",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 8, color: OFFWHITE, fontWeight: 700, fontFamily: FONT
                    }}>AU</div>
                    <div style={{
                      background: "#F4F7FB", padding: "13px 16px",
                      borderRadius: "2px 8px 8px 8px",
                      display: "flex", gap: 5, alignItems: "center",
                      borderLeft: `3px solid ${TEAL}`
                    }}>
                      {[0,1,2].map(i => (
                        <div key={i} style={{
                          width: 7, height: 7, borderRadius: "50%", background: TEAL,
                          animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`
                        }} />
                      ))}
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input area */}
              <div style={{
                padding: "14px 18px",
                borderTop: "1px solid #cdd6e2",
                background: "#F7F9FC",
                borderRadius: "0 0 8px 8px",
                flexShrink: 0
              }}>
                <label style={{
                  display: "block", fontSize: 10, fontWeight: 700, color: "#5a6a7e",
                  letterSpacing: "0.6px", marginBottom: 7, fontFamily: FONT, textTransform: "uppercase"
                }}>Enter your query</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="e.g. What are the APS procurement thresholds?"
                    rows={2}
                    style={{
                      flex: 1, padding: "10px 13px",
                      border: "1px solid #bfcfe0", borderRadius: 5,
                      resize: "none", fontFamily: FONT, fontSize: 13.5,
                      outline: "none", lineHeight: 1.5, color: "#1a2535", background: "white"
                    }}
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || loading}
                    style={{
                      padding: "10px 18px",
                      background: input.trim() && !loading ? NAVY : "#bfcfe0",
                      color: input.trim() && !loading ? OFFWHITE : "#8a9ab0",
                      border: "none", borderRadius: 5,
                      cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                      fontFamily: FONT, fontSize: 13, fontWeight: 600,
                      whiteSpace: "nowrap", alignSelf: "flex-end", letterSpacing: "0.2px"
                    }}
                  >Submit Query</button>
                </div>
                <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 6, fontFamily: FONT }}>
                  Enter to submit · Shift+Enter for new line
                </div>
              </div>
            </div>

            {/* Common queries */}
            <div style={{
              background: "white", borderRadius: 8,
              boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              padding: "14px 16px", border: "1px solid #cdd6e2"
            }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: "#5a6a7e",
                letterSpacing: "0.8px", marginBottom: 10,
                textTransform: "uppercase", fontFamily: FONT
              }}>Common Queries</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 7 }}>
                {SUGGESTED.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    disabled={loading}
                    style={{
                      textAlign: "left", padding: "9px 12px",
                      background: "#F4F7FB", border: "1px solid #cdd6e2",
                      borderRadius: 5, cursor: loading ? "not-allowed" : "pointer",
                      fontFamily: FONT, fontSize: 12.5, color: "#334155", lineHeight: 1.4
                    }}
                  >{q}</button>
                ))}
              </div>
            </div>

            {/* Repository stats */}
            <div style={{ background: NAVY, borderRadius: 8, padding: "14px 18px" }}>
              <div style={{
                fontSize: 10, color: `${OFFWHITE}60`,
                letterSpacing: "0.8px", marginBottom: 10,
                textTransform: "uppercase", fontWeight: 700, fontFamily: FONT
              }}>Document Repository</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
                {[
                  ["Documents", docsLoading ? "…" : documentStore.length],
                  ["Categories", docsLoading ? "…" : [...new Set(documentStore.map(d => d.category))].length],
                  ["Classification", "OFFICIAL"],
                  ["Jurisdiction", "Australian Gov"]
                ].map(([label, val]) => (
                  <div key={label}>
                    <span style={{ color: `${OFFWHITE}50`, display: "block", fontSize: 9, fontFamily: FONT, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</span>
                    <span style={{ color: TEAL_LT, fontWeight: 700, fontSize: 13, fontFamily: FONT }}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ INDUCTION TAB ═════════════════════════════════════════════════════ */}
        {activeTab === "induction" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{
              background: "white", borderRadius: 8,
              boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
              padding: 20, border: "1px solid #cdd6e2"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 17, color: NAVY, fontFamily: FONT, fontWeight: 600 }}>New Starter Induction</h2>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: "#5a6a7e", fontFamily: FONT }}>Complete all mandatory items within your first 10 business days</p>
                </div>
                <div style={{
                  width: 50, height: 50, flexShrink: 0,
                  background: completedCount === INDUCTION_CHECKLIST.length ? TEAL : "#EEF3FA",
                  borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  border: `2px solid ${completedCount === INDUCTION_CHECKLIST.length ? TEAL : "#cdd6e2"}`
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, fontFamily: FONT, color: completedCount === INDUCTION_CHECKLIST.length ? "white" : NAVY }}>
                    {completedCount}/{INDUCTION_CHECKLIST.length}
                  </span>
                </div>
              </div>
              <div style={{ background: "#cdd6e2", borderRadius: 3, height: 4, marginBottom: 16 }}>
                <div style={{
                  width: `${(completedCount / INDUCTION_CHECKLIST.length) * 100}%`,
                  height: "100%", borderRadius: 3, background: TEAL, transition: "width 0.4s ease"
                }} />
              </div>
              {INDUCTION_CHECKLIST.map(item => {
                const doc = documentStore.find(d => d.id === item.docId);
                const done = inductionProgress[item.id];
                return (
                  <div key={item.id} style={{
                    display: "flex", gap: 12, alignItems: "flex-start",
                    padding: "11px 0", borderBottom: "1px solid #EEF3FA"
                  }}>
                    <button
                      onClick={() => setInductionProgress(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                      style={{
                        width: 22, height: 22, borderRadius: 4, flexShrink: 0, marginTop: 1,
                        background: done ? TEAL : "white",
                        border: done ? "none" : "2px solid #bfcfe0",
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        color: "white", fontSize: 13
                      }}
                    >{done ? "✓" : ""}</button>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3, flexWrap: "wrap" }}>
                        <span style={{
                          fontSize: 13.5, fontWeight: 500, fontFamily: FONT,
                          color: done ? "#9CA3AF" : "#1a2535",
                          textDecoration: done ? "line-through" : "none"
                        }}>{item.title}</span>
                        {item.urgent && !done && (
                          <span style={{
                            fontSize: 10, background: "#FEF2F2", color: "#DC2626",
                            border: "1px solid #FECACA", borderRadius: 3,
                            padding: "1px 5px", fontFamily: FONT
                          }}>Week 1</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11.5, color: "#5a6a7e", fontFamily: FONT }}>
                        {doc?.version || "—"} · {item.time}
                      </div>
                    </div>
                    <button
                      onClick={() => { setActiveTab("chat"); setTimeout(() => sendMessage(`What are the key requirements from ${item.title}?`), 100); }}
                      style={{
                        padding: "5px 10px", background: "#EEF3FA",
                        border: "1px solid #bfcfe0", borderRadius: 4,
                        fontSize: 11, color: NAVY, cursor: "pointer",
                        fontFamily: FONT, flexShrink: 0
                      }}
                    >Ask Assist →</button>
                  </div>
                );
              })}
            </div>

            <div style={{ background: NAVY, borderRadius: 8, padding: 20, color: "white" }}>
              <h3 style={{ margin: "0 0 8px", fontSize: 15, fontFamily: FONT, fontWeight: 600, color: OFFWHITE }}>Day One Checklist</h3>
              <p style={{ margin: "0 0 12px", fontSize: 12, color: `${OFFWHITE}70`, lineHeight: 1.6, fontFamily: FONT }}>Per APS Onboarding Guidelines v2.3:</p>
              {["Meet your manager and buddy", "Complete security induction", "Obtain building access card", "Set up IT access (within 2 business days)", "Sign Acceptable Use Policy", "Review APS Code of Conduct"].map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "center" }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%",
                    background: "rgba(13,148,136,0.2)", border: `1px solid ${TEAL}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, color: TEAL_LT, fontWeight: 700, flexShrink: 0, fontFamily: FONT
                  }}>{i + 1}</div>
                  <span style={{ fontSize: 13, color: `${OFFWHITE}CC`, fontFamily: FONT }}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ DOCUMENT REPOSITORY TAB ═══════════════════════════════════════════ */}
        {activeTab === "docs" && (
          <div>
            {selectedDoc ? (
              <div>
                <button
                  onClick={() => setSelectedDoc(null)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: TEAL, fontSize: 13, marginBottom: 14, fontFamily: FONT, padding: 0
                  }}
                >← Back to Document Repository</button>
                <div style={{ background: "white", borderRadius: 8, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", overflow: "hidden", border: "1px solid #cdd6e2" }}>
                  <div style={{ background: NAVY, padding: "18px 20px" }}>
                    <div style={{ fontSize: 9, color: `${OFFWHITE}55`, letterSpacing: "1.5px", marginBottom: 5, textTransform: "uppercase", fontFamily: FONT }}>{selectedDoc.category}</div>
                    <h2 style={{ margin: "0 0 10px", color: OFFWHITE, fontSize: 17, fontFamily: FONT, fontWeight: 600 }}>{selectedDoc.title}</h2>
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                      {[["Owner", selectedDoc.owner], ["Version", selectedDoc.version], ["Date", selectedDoc.date], ["Classification", selectedDoc.classification]].map(([k, v]) => (
                        <div key={k}>
                          <div style={{ fontSize: 9, color: `${OFFWHITE}50`, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: FONT }}>{k}</div>
                          <div style={{ fontSize: 12, color: `${OFFWHITE}BB`, fontFamily: FONT }}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ padding: 20 }}>
                    <div style={{
                      fontSize: 13, lineHeight: 1.8, color: "#334155", whiteSpace: "pre-wrap",
                      background: "#F7F9FC", padding: 16, borderRadius: 6,
                      border: "1px solid #cdd6e2", fontFamily: FONT
                    }}>{selectedDoc.content}</div>
                    <button
                      onClick={() => { setActiveTab("chat"); setTimeout(() => sendMessage(`What are the key requirements from ${selectedDoc.title}?`), 100); }}
                      style={{
                        marginTop: 14, padding: "9px 18px", background: NAVY,
                        color: OFFWHITE, border: "none", borderRadius: 5,
                        cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600
                      }}
                    >Query via Assist →</button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center" }}>
                  <input
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search repository…"
                    style={{
                      flex: 1, padding: "9px 13px",
                      border: "1px solid #bfcfe0", borderRadius: 5,
                      fontFamily: FONT, fontSize: 13, outline: "none", background: "white"
                    }}
                  />
                  <div style={{ fontSize: 12, color: "#5a6a7e", whiteSpace: "nowrap", fontFamily: FONT }}>
                    {filteredDocs.length} documents
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 12 }}>
                  {filteredDocs.map(doc => {
                    const colors = categoryColors[doc.category] || { bg: "#F7F9FC", text: "#334155", border: "#cdd6e2" };
                    return (
                      <div
                        key={doc.id}
                        onClick={() => setSelectedDoc(doc)}
                        style={{
                          background: "white", borderRadius: 7,
                          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                          padding: 15, cursor: "pointer", border: "1px solid #cdd6e2"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 600, background: colors.bg,
                            color: colors.text, border: `1px solid ${colors.border}`,
                            borderRadius: 3, padding: "2px 7px", fontFamily: FONT
                          }}>{doc.category}</span>
                          <span style={{
                            fontSize: 10, background: "#F0FDF4", color: "#16A34A",
                            border: "1px solid #BBF7D0", borderRadius: 3,
                            padding: "2px 7px", fontFamily: FONT
                          }}>{doc.version} ✓</span>
                        </div>
                        <h3 style={{ margin: "0 0 5px", fontSize: 13.5, color: NAVY, lineHeight: 1.3, fontFamily: FONT, fontWeight: 600 }}>{doc.title}</h3>
                        <div style={{ fontSize: 11, color: "#5a6a7e", marginBottom: 9, fontFamily: FONT }}>{doc.owner}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 10.5, color: "#9CA3AF", fontFamily: FONT }}>Updated {doc.date}</span>
                          <span style={{
                            fontSize: 10, background: "#EEF3FA", color: NAVY,
                            borderRadius: 3, padding: "2px 7px", fontFamily: FONT
                          }}>{doc.classification}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ ADMIN TAB ════════════════════════════════════════════════════════ */}
        {activeTab === "admin" && (
          <AdminPortal
            onNavigate={setActiveTab}
            documentStore={documentStore}
            onDocumentsChange={loadDocuments}
            backendUrl={BACKEND_URL}
          />
        )}
      </div>

      {/* ══ FOOTER ══════════════════════════════════════════════════════════════ */}
      <div style={{
        textAlign: "center", padding: "14px 24px",
        fontSize: 11, color: "#8a9ab0",
        borderTop: "1px solid #cdd6e2", marginTop: 20,
        background: "white", fontFamily: FONT
      }}>
        <div style={{ fontWeight: 500, color: "#5a6a7e" }}>AUSOVRN™ Assist · Public Edition · Proof of Concept</div>
        <div style={{ color: "#a0aec0", marginTop: 3 }}>
          Enterprise deployment available for government and regulated environments
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%,80%,100% { opacity:.3; transform:scale(.8); }
          40% { opacity:1; transform:scale(1); }
        }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: #EEF2F7; }
        ::-webkit-scrollbar-thumb { background: #bfcfe0; border-radius: 3px; }
      `}</style>
    </div>
  );
}
